import { LoadingOverlay, Modal, useMantineTheme } from '@mantine/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { completionTimeWhenChapterEnds } from './completionTimeWhenChapterEnds';
import { useCourseVideoProgressInterval } from '../../hooks/useCourseVideoProgressInterval';
import { config } from '../../config/env';

interface ICourse {
  id: number;
  user_id: number;
  current_section: number;
  current_time: number;
  unlocked?: boolean;
  ecm: number;
  chapters?: Array<{ order: number; duration: number }>;
}

interface IChapter {
  id: number;
  title: string;
  order: number;
  video_url?: string;
  url?: string;
  /** Official section length (seconds); used so progress API accepts completion time */
  duration?: number;
}

interface VideoComponentProps {
  c: ICourse;
  currentChapter: IChapter | null;
  setCurrentChapter: (chapter: IChapter | null) => void;
  callback: () => void;
}

export default function VideoComponent({
  c,
  currentChapter,
  setCurrentChapter,
  callback,
}: VideoComponentProps) {
  const theme = useMantineTheme();
  const [loading, setLoading] = useState<boolean>(true);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoNode, setVideoNode] = useState<HTMLVideoElement | null>(null);

  // Persist across renders
  const lastReportedTimeRef = useRef<number>(Number(c.current_time) || 0);
  const isCompletedRef = useRef<boolean>(false);
  const initializedRef = useRef<boolean>(false);

  // API progress
  const updateCourseProgress = useCallback(
    async (currentSection: number, currentTime: number) => {
      if (c.unlocked === false) return;

      try {
        const response = await fetch(
          `${config.API_URL}/services/userCourses/${c.id}`,
          {
            method: 'POST',
            body: JSON.stringify({
              currentSection: Number(currentSection),
              currentTime: Number(currentTime),
            }),
            credentials: 'include',
            headers: { 'Content-type': 'application/json; charset=UTF-8' },
          }
        );

        if (!response.ok) {
          const text = await response.text();
          if (response.status === 404) {
            // opzionale: inizializzazione lato backend
            return;
          }
          throw new Error(`HTTP ${response.status}: ${text}`);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error occurred';
        console.error({
          color: 'red',
          withBorder: true,
          radius: 'lg',
          title: 'An error occurred',
          autoClose: false,
          message: errorMessage,
        });
      }
    },
    [c.id, c.unlocked]
  );

  // Combined ref
  const handleVideoRef = useCallback((node: HTMLVideoElement | null) => {
    videoRef.current = node;
    setVideoNode(node);
  }, []);
  const getVideoElement = useCallback(() => videoRef.current, []);

  const watchedSection =
    Number(currentChapter?.order ?? c.current_section) || 0;
  const dbSection = Number(c.current_section) || 0;
  const dbTime = Number(c.current_time) || 0;

  const canSeekByDbProgress = useCallback(
    (playbackTime: number) => {
      const chapterOrder = Number(currentChapter?.order ?? 0);
      if (chapterOrder < dbSection) {
        return true;
      }
      if (chapterOrder === dbSection) {
        return playbackTime <= dbTime;
      }
      return false;
    },
    [currentChapter?.order, dbSection, dbTime]
  );

  useCourseVideoProgressInterval({
    enabled: Boolean(currentChapter && videoNode),
    unlocked: c.unlocked,
    watchedSection,
    getVideoElement,
    onProgressTick: updateCourseProgress,
  });

  useEffect(() => {
    if (!currentChapter || !videoNode) return;

    // Stato iniziale
    isCompletedRef.current = false;

    const currentSection = dbSection;
    const currentTime = dbTime;
    const isCurrentSectionChapter = currentChapter.order === currentSection;
    const initialPlaybackTime = isCurrentSectionChapter ? currentTime : 0;
    lastReportedTimeRef.current = initialPlaybackTime;
    videoNode.currentTime = initialPlaybackTime;

    setLoading(false);
    if (currentTime === 0 && !initializedRef.current) {
      initializedRef.current = true;
      updateCourseProgress(0, 0);
    }

    // === Handlers ===
    function onSeeking() {
      if (!videoNode) return;
      const seekAllowed = canSeekByDbProgress(videoNode.currentTime);
      if (seekAllowed) return;
      const chapterOrder = Number(currentChapter?.order ?? 0);
      const maxAllowedTime =
        chapterOrder === dbSection ? dbTime : lastReportedTimeRef.current;
      const rewindTarget = Math.min(
        lastReportedTimeRef.current,
        maxAllowedTime
      );
      if (videoNode.currentTime !== rewindTarget) {
        videoNode.currentTime = rewindTarget;
      }
    }

    function onTimeUpdate() {
      if (!videoNode || isCompletedRef.current) return;
      const ct = Math.floor(videoNode.currentTime);
      const seekAllowedAtCt = canSeekByDbProgress(ct);
      // Never force rewind during normal playback; this avoids play/pause loops.
      // Seeking constraints are enforced in `onSeeking`.
      if (seekAllowedAtCt && ct > lastReportedTimeRef.current) {
        lastReportedTimeRef.current = ct;
      }
    }

    async function onEnded() {
      setLoading(true);
      isCompletedRef.current = true;

      // API persists progress for the section being completed; current_section may not increment
      // until later — parent stepper derives the next step from current_time vs catalog duration.
      const videoFloor =
        videoRef.current?.duration && Number.isFinite(videoRef.current.duration)
          ? Math.floor(videoRef.current.duration)
          : null;
      const completionTime = completionTimeWhenChapterEnds(
        currentChapter?.duration,
        videoFloor,
        lastReportedTimeRef.current
      );

      try {
        await updateCourseProgress(watchedSection, completionTime);
      } catch (e) {
        console.error(e);
      } finally {
        setCurrentChapter(null);
        callback(); // refetch parent → aggiorna Stepper
        setLoading(false);
      }
    }

    function rateChange() {
      if (
        videoNode &&
        !canSeekByDbProgress(videoNode.currentTime) &&
        videoNode.playbackRate !== 1
      ) {
        videoNode.playbackRate = 1;
      }
    }

    function volumeChanged() {
      if (!videoNode || canSeekByDbProgress(videoNode.currentTime)) return;
      if (videoNode.volume < 0.2) {
        videoNode.volume = 0.2; // minimo 20%
      }
      if (videoNode.muted) {
        videoNode.muted = false;
      }
    }

    // Fine video: sempre (salva progresso, refetch parent / stepper). Senza questo, i corsi
    // non-ECM (canSeek sempre true) non aggiornavano mai lo stato a video terminato.
    videoNode.addEventListener('ended', onEnded);

    // I listener rimangono sempre attivi: la condizione DB viene verificata runtime.
    videoNode.addEventListener('seeking', onSeeking);
    videoNode.addEventListener('timeupdate', onTimeUpdate);
    videoNode.addEventListener('ratechange', rateChange);
    videoNode.addEventListener('volumechange', volumeChanged);

    return () => {
      videoNode.removeEventListener('ended', onEnded);
      videoNode.removeEventListener('seeking', onSeeking);
      videoNode.removeEventListener('timeupdate', onTimeUpdate);
      videoNode.removeEventListener('ratechange', rateChange);
      videoNode.removeEventListener('volumechange', volumeChanged);
    };
  }, [
    videoNode,
    currentChapter,
    c.current_section,
    c.current_time,
    c.chapters,
    c.ecm,
    setCurrentChapter,
    callback,
    updateCourseProgress,
    watchedSection,
    canSeekByDbProgress,
    dbSection,
    dbTime,
  ]);

  const canSeek = canSeekByDbProgress(videoNode?.currentTime ?? 0);

  return (
    <Modal
      opened={!!currentChapter}
      size={'xl'}
      title={currentChapter?.title || ''}
      onClose={() => {
        setCurrentChapter(null);
      }}
    >
      <div style={{ position: 'relative' }}>
        <LoadingOverlay visible={loading} />
        <video
          key={currentChapter?.id} // forza remount al cambio capitolo
          width='100%'
          height='auto'
          disablePictureInPicture
          controlsList={canSeek ? 'nodownload' : 'nodownload noseek'}
          controls
          style={{
            borderRadius: 6,
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: theme.colors.gray[5],
            outline: 'none',
            boxShadow: 'none',
          }}
          ref={handleVideoRef}
        >
          <source
            src={currentChapter?.video_url || currentChapter?.url || ''}
            type='video/mp4'
          />
          Your browser does not support the video tag.
        </video>
      </div>
    </Modal>
  );
}
